import classNames from 'classnames';
import React, { useEffect, useMemo, useState } from 'react';
import type { SetNonNullable } from 'type-fest';
import Avatar from '../../components/Avatar';
import Button from '../../components/Button';
import Tooltip from '../../components/Tooltip';
import { useActiveGutterBlot } from '../../editor/Gutter';
import type { HoveredBlotListenerSetting } from '../../editor/HoveredBlotListener';
import { useHoveredBlotListener } from '../../editor/HoveredBlotListener';
import { useQuill } from '../../editor/QuillContext';
import Header from '../../editor/formats/Header';
import {
  USER_SEQUENCE_ATTRIBUTE,
  getUserSequence,
} from '../../editor/formats/UserSequence';
import { useGetUser } from '../../user/useAllUsers';
import {
  PostLayoutSize,
  usePostLayoutSize,
} from '../../utils/responsive/PostLayoutSizeProvider';
import MagneticWallLayout, {
  useMagneticWallLayout,
} from '../MagneticWallLayout';
import type { RelativeDOMRect } from '../utils/toRelativeDOMRect';
import toRelativeDOMRect from '../utils/toRelativeDOMRect';
import * as styles from './EditFacesMagneticWall.module.scss';
import {
  getVersionChangeContentRects,
  moveFocusedClass,
  useOnVersionChangeClick,
  useVersionChanges,
  useVersionChangesFocus,
} from './VersionChanges';
import { useVersionColorMode } from './VersionColorMode';
import type { VersionColorMode } from './VersionColorMode';
import type { EditVersionChange } from './calculateVersionChanges';

const AVATAR_SIZE = parseInt(styles.avatarSize, 10);
const SPACING = parseInt(styles.spacing, 10);
const SPACING_DIFF_MODE = parseInt(styles.spacingDiffMode, 10);

interface EditFaceProps {
  versionChange: EditVersionChange;
  isInitiallyShowingTooltip?: boolean;
  onClick: () => void;
  focused: boolean;
  colorMode: VersionColorMode;
}

// eslint-disable-next-line import/no-unused-modules -- exported for testing
export const EditFace = ({
  versionChange,
  isInitiallyShowingTooltip,
  onClick,
  focused,
  colorMode,
}: EditFaceProps) => {
  const getUser = useGetUser();
  const author = getUser({ id: versionChange.authorId });

  return (
    <div className={styles.editFaceContainer}>
      <Tooltip
        open={isInitiallyShowingTooltip}
        asChild
        content={
          <div className={styles.tooltipContainer}>
            <div className={styles.tooltipName}>{author.name}</div>
            <Tooltip.Muted className={styles.tooltipTitle}>
              {author.title}
            </Tooltip.Muted>
          </div>
        }
      >
        <div
          {...(colorMode !== 'diff'
            ? {
                [USER_SEQUENCE_ATTRIBUTE]: getUserSequence(
                  versionChange.authorId,
                ),
              }
            : {})}
          className={styles.avatarButtonContainer}
        >
          <Button
            className={classNames(styles.avatarButton, {
              [styles.hasRing]: focused || colorMode !== 'diff',
              [styles.muted]: versionChange.isFromPreviousVersion && !focused,
            })}
            aria-label={author.name}
            onClick={onClick}
          >
            <Avatar size={AVATAR_SIZE} user={author} />
          </Button>
        </div>
      </Tooltip>
    </div>
  );
};

interface InnerProps {
  versionChanges: EditVersionChange[];
  hoveredBlotRect: RelativeDOMRect | null;
  colorMode: VersionColorMode;
}

const Inner = ({ versionChanges, hoveredBlotRect, colorMode }: InnerProps) => {
  const { quill } = useQuill();
  const [focusedPreviousVersionChangeId, setFocusedPreviousVersionChangeId] =
    useState<string | null>(null);
  const { layout, debouncedUpdateDesiredPositions, updateThreadHeights } =
    useMagneticWallLayout();
  const { focusedVersionChange, focusById } = useVersionChangesFocus();

  const visibleItems = useMemo(
    () =>
      versionChanges
        .map((item) => {
          const isOverlapped = layout.overlappedIds.has(item.id);
          const top = layout.tops[item.id];

          if (top != null && hoveredBlotRect != null) {
            const bottom = top + AVATAR_SIZE;

            const isBelow = top > hoveredBlotRect.bottom;
            const isAbove = bottom < hoveredBlotRect.top;

            const isIntersectingAvatar = !isBelow && !isAbove;

            return {
              ...item,
              top,
              isInBackground: isIntersectingAvatar,
              isOverlapped,
            };
          }

          return {
            ...item,
            top,
            isInBackground: false,
            isOverlapped,
          };
        })
        .filter(
          (item): item is SetNonNullable<typeof item, 'top'> =>
            item.top != null,
        ),
    [layout.overlappedIds, layout.tops, versionChanges, hoveredBlotRect],
  );

  useEffect(() => {
    debouncedUpdateDesiredPositions();
    updateThreadHeights();
  }, [visibleItems, debouncedUpdateDesiredPositions, updateThreadHeights]);

  useEffect(() => {
    if (!quill) return;
    moveFocusedClass(quill.root, focusedPreviousVersionChangeId, true);
  }, [quill, focusedPreviousVersionChangeId]);

  useOnVersionChangeClick(setFocusedPreviousVersionChangeId, {
    isFromPreviousVersion: true,
    skip: colorMode !== 'full',
  });

  return (
    <>
      {visibleItems.map((versionChange) => {
        const isFocused =
          versionChange.id === focusedVersionChange?.id ||
          versionChange.id === focusedPreviousVersionChangeId;

        return (
          <MagneticWallLayout.Item
            key={versionChange.id}
            id={versionChange.id}
            focused={isFocused}
            top={versionChange.top}
            className={classNames({
              [styles.isInBackground]: versionChange.isInBackground,
            })}
          >
            <EditFace
              versionChange={versionChange}
              onClick={() => {
                if (versionChange.isFromPreviousVersion) {
                  setFocusedPreviousVersionChangeId(versionChange.id);
                } else {
                  focusById(versionChange.id);
                }
              }}
              focused={isFocused}
              colorMode={colorMode}
            />
          </MagneticWallLayout.Item>
        );
      })}
    </>
  );
};

const hoveredBlotListenerSetting: HoveredBlotListenerSetting<
  Record<string, unknown>,
  Header
> = {
  isHoveredBlot(blot) {
    return blot instanceof Header;
  },
};

const EditFacesMagneticWall = () => {
  const activeGutterBlot = useActiveGutterBlot();
  const { versionChanges } = useVersionChanges();
  const { focusedVersionChange } = useVersionChangesFocus();
  const colorMode = useVersionColorMode();

  const editVersionChanges = useMemo(
    () =>
      versionChanges.filter(
        (v): v is EditVersionChange =>
          v.type === 'edit' &&
          (colorMode === 'full' || !v.isFromPreviousVersion),
      ),
    [versionChanges, colorMode],
  );

  const { quill } = useQuill();

  const [hoveredBlotRect, setHoveredBlotRect] =
    useState<RelativeDOMRect | null>(null);

  const { hoveredBlot: hoveredHeader } = useHoveredBlotListener(
    quill,
    hoveredBlotListenerSetting,
  );

  const hoveredBlot = activeGutterBlot ?? hoveredHeader;

  useEffect(() => {
    if (!quill) return;

    if (hoveredBlot) {
      setHoveredBlotRect(
        toRelativeDOMRect(
          hoveredBlot.domNode.getBoundingClientRect(),
          quill.container.getBoundingClientRect(),
        ),
      );
    } else {
      setHoveredBlotRect(null);
    }
  }, [hoveredBlot, quill]);

  const postLayoutSize = usePostLayoutSize();

  if (
    postLayoutSize &&
    [PostLayoutSize.mobile, PostLayoutSize.tablet].includes(postLayoutSize)
  ) {
    return null;
  }

  return (
    <div
      className={classNames(styles.container, {
        [styles.hasHoveredBlot]: hoveredBlot != null,
      })}
    >
      <MagneticWallLayout.Root
        itemSize="gutter"
        magneticWallItems={editVersionChanges}
        focus={focusedVersionChange}
        getRelativeContentRects={() =>
          quill ? getVersionChangeContentRects(quill) : []
        }
        spacing={colorMode === 'diff' ? SPACING_DIFF_MODE : SPACING}
        minItemHeight={AVATAR_SIZE}
      >
        <Inner
          versionChanges={editVersionChanges}
          hoveredBlotRect={hoveredBlotRect}
          colorMode={colorMode}
        />
      </MagneticWallLayout.Root>
    </div>
  );
};

export default EditFacesMagneticWall;
